import React, { useCallback, useMemo, useState } from 'react';
import { Keyboard, StyleSheet, Text, View } from 'react-native';
import Feather from '@react-native-vector-icons/feather/static';
import { useKeyboardState } from 'react-native-keyboard-controller';
import type { ICountry } from 'rn-international-phone-number';
import { GTFonts, useTheme } from '../../theme';
import { borderWidth, fs, iconSize, s, v } from '../../utils/libs';
import type { PickerRow } from '../../utils/customerPicker';
import type { Customer } from '../../types/customers';
import CustomerPickerList, {
  type CustomerPickerListHandlers,
} from './CustomerPickerList';
import NewCustomerPage, { type NewCustomerFormData } from './NewCustomerPage';
import PersonAvatar from './PersonAvatar';
import PersonRow from './PersonRow';
import PickerPageHeader from './PickerPageHeader';
import PickerSearchBar from './PickerSearchBar';
import StaffAssignSection, {
  type StaffAssignModel,
} from './StaffAssignSection';
import StaffPickerPage from './StaffPickerPage';

export type PickerPage = 'list' | 'staff' | 'newCustomer';

/** A customer the order already has and keeps (a partly paid order). */
export interface LockedCustomerView {
  name: string;
  meta: string;
  linked: boolean;
  note: string;
}

export interface NewCustomerSubmitResult {
  /** Saved: the picker returns to the list. */
  saved: boolean;
  /** Not saved, as customers already have the name and no phone was sent. */
  sameName?: Customer[];
}

export interface NewCustomerModel {
  countryCode: string;
  country: ICountry | undefined;
  submitting: boolean;
  onSubmit: (data: NewCustomerFormData) => Promise<NewCustomerSubmitResult>;
  /** One of the `sameName` customers, picked instead of a new one. */
  onPickExisting: (customer: Customer) => void;
}

export interface CustomerStaffPickerProps
  extends Omit<CustomerPickerListHandlers, 'onAddNew'> {
  /** `sheet`: its own title and a list that scrolls. `embedded`: stacked inside another sheet's content. */
  variant: 'sheet' | 'embedded';
  title?: string;
  subtitle?: string;
  onClose?: () => void;
  rows: PickerRow[];
  query: string;
  onQueryChange: (query: string) => void;
  /** The keyboard's Done. */
  onSubmitQuery: () => void;
  lockedCustomer?: LockedCustomerView | null;
  /** null hides the staff section. */
  staff: StaffAssignModel | null;
  newCustomer: NewCustomerModel;
  /** Under the staff section on the list page: the sheet's save button. */
  footer?: React.ReactNode;
  onPageChange?: (page: PickerPage) => void;
}

/**
 * The customer and staff picker as the design draws it: search, one list,
 * staff strip; with the New customer form and the full staff page opening in
 * place of the list.
 */
const CustomerStaffPicker: React.FC<CustomerStaffPickerProps> = ({
  variant,
  title,
  subtitle,
  onClose,
  rows,
  query,
  onQueryChange,
  onSubmitQuery,
  lockedCustomer,
  staff,
  newCustomer,
  footer,
  onPageChange,
  onSelectWalkIn,
  onSelectLabel,
  onSelectCustomer,
  onSaveCustomer,
  onSelectContact,
  onRetryContact,
  onUseLabel,
  onAllowContacts,
  onOpenSettings,
  onRetryContacts,
  onRetryCustomers,
}) => {
  const { theme } = useTheme();
  const styles = useMemo(() => createStyles(theme), [theme]);
  const isSheet = variant === 'sheet';
  const [page, setPage] = useState<PickerPage>('list');
  const [prefillName, setPrefillName] = useState('');
  const keyboardVisible = useKeyboardState(state => state.isVisible);
  // No staff at the location, or the keyboard is up: the section goes, so the
  // list gets the room.
  const showStaff = Boolean(
    !keyboardVisible &&
      staff &&
      (staff.selfAssignedName ||
        staff.loading ||
        staff.error ||
        staff.members.length > 0),
  );

  const go = useCallback(
    (next: PickerPage) => {
      Keyboard.dismiss();
      setPage(next);
      onPageChange?.(next);
    },
    [onPageChange],
  );

  const openNewCustomer = useCallback(
    (name?: string) => {
      setPrefillName(String(name ?? query).trim());
      go('newCustomer');
    },
    [go, query],
  );

  const pickStaff = useCallback(
    (id: string | null) => {
      if (!staff) return;
      if (id === null) {
        if (staff.assignedStaffId) staff.onToggle(staff.assignedStaffId);
      } else if (id !== staff.assignedStaffId) {
        staff.onToggle(id);
      }
      go('list');
    },
    [go, staff],
  );

  if (page === 'staff' && staff) {
    return (
      <StaffPickerPage
        members={staff.members}
        assignedStaffId={staff.assignedStaffId}
        onPick={pickStaff}
        onBack={() => go('list')}
        embedded={!isSheet}
      />
    );
  }

  if (page === 'newCustomer') {
    return (
      <NewCustomerPage
        initialName={prefillName}
        subtitle={subtitle ? `For this order · ${subtitle}` : 'For this order'}
        countryCode={newCustomer.countryCode}
        country={newCustomer.country}
        submitting={newCustomer.submitting}
        onSubmit={async data => {
          const result = await newCustomer.onSubmit(data);
          if (result.saved) {
            onQueryChange('');
            go('list');
          }
          return result.sameName ?? null;
        }}
        onPickExisting={customer => {
          newCustomer.onPickExisting(customer);
          onQueryChange('');
          go('list');
        }}
        onCancel={() => go('list')}
        embedded={!isSheet}
      />
    );
  }

  return (
    <View style={styles.page}>
      {isSheet && title ? (
        <PickerPageHeader
          title={title}
          subtitle={subtitle}
          // Settings button beside the ×, hidden until it does something:
          // onSettings={() => {}}
          onClose={onClose}
        />
      ) : null}

      {lockedCustomer ? (
        <View style={[styles.locked, isSheet && styles.lockedSheet]}>
          <Text style={styles.lockedLabel}>Customer</Text>
          <View style={styles.lockedCard}>
            <PersonRow
              leading={
                <PersonAvatar
                  name={lockedCustomer.name}
                  linked={lockedCustomer.linked}
                />
              }
              name={lockedCustomer.name}
              meta={lockedCustomer.meta}
              trailing={
                <View style={styles.lockedPill}>
                  <Feather
                    name="lock"
                    size={iconSize(12)}
                    color={theme.colors.primaryDark}
                  />
                  <Text style={styles.lockedPillText}>Set</Text>
                </View>
              }
            />
          </View>
          <Text style={styles.lockedNote}>{lockedCustomer.note}</Text>
        </View>
      ) : (
        <>
          <View
            style={[
              styles.search,
              isSheet &&
                (title ? styles.searchSheet : styles.searchSheetUntitled),
            ]}>
            <PickerSearchBar
              value={query}
              onChangeText={onQueryChange}
              placeholder="Search customers or contacts"
              onSubmit={onSubmitQuery}
              onAddNew={() => openNewCustomer()}
            />
          </View>
          <View style={isSheet ? styles.listSheet : undefined}>
            <CustomerPickerList
              rows={rows}
              scrollable={isSheet}
              onSelectWalkIn={onSelectWalkIn}
              onSelectLabel={onSelectLabel}
              onSelectCustomer={onSelectCustomer}
              onSaveCustomer={onSaveCustomer}
              onSelectContact={onSelectContact}
              onRetryContact={onRetryContact}
              onUseLabel={onUseLabel}
              onAddNew={openNewCustomer}
              onAllowContacts={onAllowContacts}
              onOpenSettings={onOpenSettings}
              onRetryContacts={onRetryContacts}
              onRetryCustomers={onRetryCustomers}
            />
          </View>
        </>
      )}

      {showStaff || footer ? (
        <View style={[styles.footer, isSheet && styles.footerSheet]}>
          {staff && showStaff ? (
            <StaffAssignSection {...staff} onOpenAll={() => go('staff')} />
          ) : null}
          {footer}
        </View>
      ) : null}
    </View>
  );
};

const createStyles = (theme: any) =>
  StyleSheet.create({
    page: {
      flex: 1,
    },
    search: {
      paddingHorizontal: s(4),
      paddingBottom: v(4),
    },
    searchSheet: {
      paddingHorizontal: s(20),
    },
    // No title of its own: in line with the host sheet's tabs (checkout).
    searchSheetUntitled: {
      paddingHorizontal: s(12),
    },
    listSheet: {
      flex: 1,
      minHeight: 0,
    },
    locked: {
      paddingHorizontal: s(4),
      paddingBottom: v(8),
      gap: v(8),
    },
    // Shown on Partial checkout only: in line with the host sheet's tabs.
    lockedSheet: {
      paddingHorizontal: s(12),
    },
    lockedLabel: {
      fontFamily: GTFonts.bold,
      fontSize: fs(11),
      lineHeight: fs(14),
      letterSpacing: 0.7,
      textTransform: 'uppercase',
      color: theme.colors.textGray500,
      paddingHorizontal: s(4),
    },
    lockedCard: {
      borderRadius: s(16),
      borderWidth: borderWidth(1),
      borderColor: theme.colors.borderGray200,
      backgroundColor: theme.colors.surface,
      paddingHorizontal: s(2),
    },
    lockedPill: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: s(5),
      paddingHorizontal: s(9),
      paddingVertical: v(4),
      borderRadius: s(8),
      backgroundColor: theme.colors.primarySurface,
    },
    lockedPillText: {
      fontFamily: GTFonts.bold,
      fontSize: fs(12),
      lineHeight: fs(15),
      color: theme.colors.primaryDark,
    },
    lockedNote: {
      fontFamily: GTFonts.regular,
      fontSize: fs(13),
      lineHeight: fs(18),
      color: theme.colors.textGray600,
      paddingHorizontal: s(4),
    },
    footer: {
      gap: v(6),
      paddingTop: v(12),
      paddingHorizontal: s(4),
    },
    footerSheet: {
      paddingHorizontal: s(20),
      paddingBottom: v(16),
      borderTopWidth: borderWidth(1),
      borderTopColor: theme.colors.border,
      backgroundColor: theme.colors.surface,
    },
  });

export default CustomerStaffPicker;
